import { useEffect, useMemo, useState } from 'react'
import type { AgenteMonedaView, KeyValue, PagaebleResult, RamoDTO, ReporteReciboDTO, WrapperResponse } from '../../../model/kartera-api-model'
import APIInvoke, { LONG_TIMEOUT_MS } from '../../../utils/APIInvoke'
import { daysFromToday } from '../../../utils/dates'
import {
    ALL_ROWS,
    breakdown,
    catalogLabel,
    defaultRange,
    downloadCsv,
    errorMessage,
    formatAmount,
    formatSlashDate,
    formatSlashDateTime,
    loadCatalog,
    matchesSearch,
    PAGE_SIZES,
    pageWindow,
    paginate,
    personName,
    totalLabel,
} from '../common/report-common'
import type { BreakdownDimension, ReportColumn } from '../common/report-common'

const LOAD_ERROR = 'No se pudo cargar el reporte de recibos.'
const RANGE_ERROR = 'La fecha final debe ser igual o posterior a la inicial.'

export interface ReceiptReportFilters {
    vigenciaDe: string
    vigenciaA: string
    status: string
    ramo: string
    formaPago: string
    moneda: string
}

export interface ReceiptReportRow {
    id: number
    polizaId: number
    noPoliza: string
    serie: string
    contratante: string
    concepto: string
    paquete: string
    aseguradora: string
    clave: string
    ramo: string
    statusKey: string
    status: string
    formaPago: string
    vigenciaDe: string
    vigenciaA: string
    vence: string
    venceDays: number | null
    creacion: string
    prima: number
    total: number
    moneda: string
    robot: boolean
}

const defaultFilters = (): ReceiptReportFilters => {
    const range = defaultRange()
    return { vigenciaDe: range.from, vigenciaA: range.to, status: '', ramo: '', formaPago: '', moneda: '' }
}

const buildQuery = (filters: ReceiptReportFilters) => {
    const params = [`page=1`, `size=${ALL_ROWS}`]
    if (filters.vigenciaDe) params.push(`fecVigDe[ge]=${filters.vigenciaDe}`)
    if (filters.vigenciaA) params.push(`fecVigDe[le]=${filters.vigenciaA}`)
    if (filters.status) params.push(`status=${encodeURIComponent(filters.status)}`)
    if (filters.ramo) params.push(`ramo=${encodeURIComponent(filters.ramo)}`)
    if (filters.formaPago) params.push(`formaPago=${encodeURIComponent(filters.formaPago)}`)
    if (filters.moneda) params.push(`moneda=${encodeURIComponent(filters.moneda)}`)
    return params.join('&')
}

const toRow = (item: ReporteReciboDTO, statuses: KeyValue[], formas: KeyValue[]): ReceiptReportRow => ({
    id: item.reciboId,
    polizaId: item.polizaId,
    noPoliza: String(item.noPoliza || '').toUpperCase(),
    serie: item.serie != null ? `${item.serie}/${item.totalSerie ?? '—'}` : '—',
    contratante: personName(item),
    concepto: item.concepto || '',
    paquete: item.paquete || '',
    aseguradora: item.aseguradora || '',
    clave: item.clave || '',
    ramo: item.ramo || '',
    statusKey: String(item.status || '').toLowerCase(),
    status: catalogLabel(statuses, item.status),
    formaPago: catalogLabel(formas, item.formaPago),
    vigenciaDe: formatSlashDate(item.fecVigDe) || '',
    vigenciaA: formatSlashDate(item.fecVigA) || '',
    vence: formatSlashDate(item.fecVence) || '',
    venceDays: daysFromToday(item.fecVence),
    creacion: formatSlashDateTime(item.fecha) || '',
    prima: Number(item.prima) || 0,
    total: Number(item.total) || 0,
    moneda: item.moneda || 'MXN',
    robot: Boolean(item.esRobot),
})

export const CSV_COLUMNS: ReportColumn<ReceiptReportRow>[] = [
    { key: 'noPoliza', label: 'No. póliza', csv: (row) => row.noPoliza },
    { key: 'serie', label: 'Serie', csv: (row) => row.serie },
    { key: 'contratante', label: 'Contratante', csv: (row) => row.contratante },
    { key: 'concepto', label: 'Concepto', csv: (row) => row.concepto },
    { key: 'paquete', label: 'Paquete', csv: (row) => row.paquete },
    { key: 'aseguradora', label: 'Aseguradora', csv: (row) => row.aseguradora },
    { key: 'clave', label: 'Clave', csv: (row) => row.clave },
    { key: 'ramo', label: 'Ramo', csv: (row) => row.ramo },
    { key: 'status', label: 'Estatus', csv: (row) => row.status },
    { key: 'formaPago', label: 'Forma de pago', csv: (row) => row.formaPago },
    { key: 'vigenciaDe', label: 'Vigencia de', csv: (row) => row.vigenciaDe },
    { key: 'vigenciaA', label: 'Vigencia a', csv: (row) => row.vigenciaA },
    { key: 'vence', label: 'Vencimiento', csv: (row) => row.vence },
    { key: 'creacion', label: 'Fecha de creación', csv: (row) => row.creacion },
    { key: 'prima', label: 'Prima neta', csv: (row) => row.prima.toFixed(2) },
    { key: 'total', label: 'Prima total', csv: (row) => row.total.toFixed(2) },
    { key: 'moneda', label: 'Moneda', csv: (row) => row.moneda },
    { key: 'robot', label: 'Robot', csv: (row) => (row.robot ? 'Sí' : 'No') },
]

export const STATUS_TONES: Record<string, string> = {
    pendiente: 'bg-tone-orange text-tone-orange-ink',
    pagado: 'bg-k-ok-soft text-k-ok',
    conciliado: 'bg-tone-teal text-tone-teal-ink',
    cancelado: 'bg-k-line text-k-ink-2',
}

export const useReportReceiptsModel = () => {
    const [filters, setFilters] = useState<ReceiptReportFilters>(defaultFilters)
    const [query, setQuery] = useState(() => buildQuery(defaultFilters()))
    const [reloadToken, setReloadToken] = useState(0)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [items, setItems] = useState<ReporteReciboDTO[]>([])
    const [statuses, setStatuses] = useState<KeyValue[]>([])
    const [formas, setFormas] = useState<KeyValue[]>([])
    const [ramos, setRamos] = useState<RamoDTO[]>([])
    const [monedas, setMonedas] = useState<AgenteMonedaView[]>([])
    const [dimension, setDimension] = useState<BreakdownDimension>('ramo')
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(PAGE_SIZES[0])
    const [detailId, setDetailId] = useState<number | null>(null)

    useEffect(() => {
        let active = true
        const loadCatalogs = async () => {
            try {
                const [statusList, formaList, ramoResponse, monedaResponse] = await Promise.all([
                    loadCatalog('/catalogos/recibos/status'),
                    loadCatalog('/catalogos/forma-pago'),
                    APIInvoke.invokeGET('/ramos').then((response) => response.json() as Promise<WrapperResponse<RamoDTO[]>>),
                    APIInvoke.invokeGET('/monedas').then((response) => response.json() as Promise<WrapperResponse<AgenteMonedaView[]>>),
                ])
                if (!active) return
                setStatuses(statusList)
                setFormas(formaList)
                setRamos(ramoResponse?.ok ? ramoResponse.body || [] : [])
                setMonedas(monedaResponse?.ok ? monedaResponse.body || [] : [])
            } catch (catalogError) {
                console.error(catalogError)
            }
        }
        loadCatalogs()
        return () => { active = false }
    }, [])

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setError(null)
                const payload: WrapperResponse<PagaebleResult<ReporteReciboDTO>> | null = await (await APIInvoke.invokeGET(`/reportes/recibos?${query}`, { timeoutMs: LONG_TIMEOUT_MS })).json()
                if (!active) return
                if (!payload?.ok || !payload.body) throw new Error(payload?.message || LOAD_ERROR)
                setItems(payload.body.items || [])
                setPage(1)
            } catch (loadError) {
                if (active) {
                    setItems([])
                    setError(errorMessage(loadError, LOAD_ERROR))
                }
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [query, reloadToken])

    const rangeInvalid = Boolean(filters.vigenciaDe && filters.vigenciaA && filters.vigenciaDe > filters.vigenciaA)

    const apply = (next: ReceiptReportFilters) => {
        setFilters(next)
        const invalid = Boolean(next.vigenciaDe && next.vigenciaA && next.vigenciaDe > next.vigenciaA)
        if (!invalid) setQuery(buildQuery(next))
    }

    const change = <K extends keyof ReceiptReportFilters>(key: K, value: ReceiptReportFilters[K]) => apply({ ...filters, [key]: value })

    const allRows = useMemo(() => items.map((item) => toRow(item, statuses, formas)), [items, statuses, formas])
    const filteredRows = useMemo(
        () => allRows.filter((row) => matchesSearch(search, row.noPoliza, row.contratante, row.concepto, row.paquete, row.aseguradora, row.clave)),
        [allRows, search],
    )
    const paged = paginate(filteredRows, page, pageSize)

    const breakdowns = useMemo(() => ({
        ramo: breakdown(allRows, (row) => row.ramo, (row) => row.total),
        status: breakdown(allRows, (row) => row.status, (row) => row.total),
        aseguradora: breakdown(allRows, (row) => row.aseguradora, (row) => row.total),
    }), [allRows])

    const pendingRows = allRows.filter((row) => row.statusKey === 'pendiente')

    return {
        filters,
        rangeInvalid,
        rangeError: rangeInvalid ? RANGE_ERROR : null,
        statuses,
        formas,
        ramos,
        monedas,
        loading,
        error,
        count: allRows.length,
        totals: totalLabel(allRows, (row) => row.total, (row) => row.moneda),
        pending: { count: pendingRows.length, ...totalLabel(pendingRows, (row) => row.total, (row) => row.moneda) },
        overdue: pendingRows.filter((row) => row.venceDays != null && row.venceDays < 0).length,
        dimension,
        breakdown: breakdowns[dimension],
        search,
        rows: paged.rows,
        filteredCount: filteredRows.length,
        page: paged.page,
        totalPages: paged.totalPages,
        pages: filteredRows.length > 0 ? pageWindow(paged.page, paged.totalPages) : [],
        pageSize,
        rangeFrom: paged.rangeFrom,
        rangeTo: paged.rangeTo,
        detailId,
        formatSum: (value: number) => `$${formatAmount(value)}`,
        change,
        clearFilters: () => apply(defaultFilters()),
        changeDimension: setDimension,
        changeSearch: (value: string) => {
            setSearch(value)
            setPage(1)
        },
        setPage,
        changePageSize: (value: number) => {
            setPageSize(value)
            setPage(1)
        },
        exportCsv: () => downloadCsv('reporte-recibos', CSV_COLUMNS, filteredRows),
        reload: () => setReloadToken((token) => token + 1),
        openPoliza: (id: number) => setDetailId(id),
        closePoliza: () => setDetailId(null),
    }
}
